import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { gsap } from "gsap";
import $ from "jquery";
import imgLocation from "../controllers/imagePath";
import ApiURl from "../controllers/Api";

// Inline fallback (no external DNS)
const FALLBACK_IMG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="300" height="300">
    <rect width="100%" height="100%" fill="#f2f2f2"/>
    <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
      font-family="Arial" font-size="18" fill="#777">
      No Image
    </text>
  </svg>
`);

/**
 * ✅ Extracted OUTSIDE Deals so it doesn't remount each render (prevents focus loss)
 */
function PriceRangeUI({
  compact = false,
  priceStats,
  priceRange,
  priceDraft,
  onSliderChange,
  onInputFocus,
  onInputChange,
  onInputCommit,
  formatPriceWithCommas,
}) {
  const min = priceStats.min;
  const max = priceStats.max;
  const range = Math.max(1, max - min);
  const minPct = ((priceRange[0] - min) / range) * 100;
  const maxPct = ((priceRange[1] - min) / range) * 100;
  // z-index swapping helps when thumbs overlap
  const minZ = priceRange[0] > max - range * 0.1 ? 40 : 30;
  const maxZ = 35;
  const halfThumbPct = 5;
  const clipRightMin = Math.max(0, 100 - minPct - halfThumbPct);
  const clipLeftMax = Math.max(0, maxPct - halfThumbPct);
  const commonRangeInput =
    "absolute top-0 left-0 w-full h-8 opacity-0 cursor-pointer touch-none";
  return (
    <div className={compact ? "space-y-3" : "space-y-4"}>
      {/* Slider */}
      <div className="relative pt-6">
        <div className="relative h-1.5 bg-gray-200 rounded-full">
          <div
            className="absolute h-1.5 bg-blue-600 rounded-full"
            style={{
              left: `${minPct}%`,
              right: `${100 - maxPct}%`,
            }}
          />
          <div
            className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-white border-2 border-blue-600 rounded-full shadow-md pointer-events-none"
            style={{ left: `${minPct}%` }}
          />
          <div
            className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-white border-2 border-blue-600 rounded-full shadow-md pointer-events-none"
            style={{ left: `${maxPct}%` }}
          />
        </div>
        {/* Max first for stacking */}
        <input
          type="range"
          min={min}
          max={max}
          step="100"
          value={priceRange[1]}
          onChange={(e) => onSliderChange(1, e.target.value)}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          className={commonRangeInput}
          style={{ zIndex: maxZ, clipPath: `inset(0 0 0 ${clipLeftMax}%)` }}
        />
        {/* Min */}
        <input
          type="range"
          min={min}
          max={max}
          step="100"
          value={priceRange[0]}
          onChange={(e) => onSliderChange(0, e.target.value)}
          onTouchStart={(e) => e.stopPropagation()}
          onTouchMove={(e) => e.stopPropagation()}
          className={commonRangeInput}
          style={{ zIndex: minZ, clipPath: `inset(0 ${clipRightMin}% 0 0)` }}
        />
        <div className="flex justify-between text-xs text-gray-500 mt-3">
          <span>{formatPriceWithCommas(min)}</span>
          <span>{formatPriceWithCommas(max)}</span>
        </div>
      </div>
      {/* Inputs */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-gray-500 mb-1 block">
            {compact ? "Min" : "Min Price"}
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
              ₹
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={priceDraft[0]}
              onFocus={() => onInputFocus(0)}
              onChange={(e) => onInputChange(0, e.target.value)}
              onBlur={() => onInputCommit(0)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              placeholder={String(min)}
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
        <div>
          <label className="text-xs text-gray-500 mb-1 block">
            {compact ? "Max" : "Max Price"}
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sm text-gray-500">
              ₹
            </span>
            <input
              type="text"
              inputMode="numeric"
              value={priceDraft[1]}
              onFocus={() => onInputFocus(1)}
              onChange={(e) => onInputChange(1, e.target.value)}
              onBlur={() => onInputCommit(1)}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.currentTarget.blur();
              }}
              placeholder={String(max)}
              className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * ✅ Extracted OUTSIDE Deals so it doesn't remount each render (prevents focus loss)
 */
function MobileFilterSidebar({
  filtersOpen,
  setFiltersOpen,
  appliedFilters,
  clearFilters,
  sort,
  setSort,
  SORT_OPTIONS,
  fetchedCategories,
  selectedCategories,
  toggleCategory,
  selectAllCategories,
  clearAllCategories,
  getProductCountForCategory,
  priceRange,
  priceStats,
  priceDraft,
  onPriceInputFocus,
  onPriceSliderChange,
  onPriceInputChange,
  onPriceInputCommit,
  formatPriceWithCommas,
  filteredCount,
}) {
  const sidebarRef = useRef(null);

  useEffect(() => {
    if (!filtersOpen) return;

    const preventBackgroundScroll = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        event.preventDefault();
      }
    };

    document.addEventListener("touchmove", preventBackgroundScroll, { passive: false });

    return () => {
      document.removeEventListener("touchmove", preventBackgroundScroll);
    };
  }, [filtersOpen]);

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 bg-black/50 z-[1000] transition-opacity duration-200 md:hidden ${
          filtersOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={() => setFiltersOpen(false)}
        aria-hidden="true"
      />
      {/* Sidebar */}
      <div
        ref={sidebarRef}
        role="dialog"
        aria-modal="true"
        className={`fixed left-0 top-0 h-full w-80 bg-white z-[1001] shadow-xl md:hidden transform transition-transform duration-300 ease-out ${
          filtersOpen ? "translate-x-0" : "-translate-x-full"
        }
        overflow-y-auto overscroll-contain
        [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden
        `}
      >
        <div className="p-4 h-full">
          <div className="flex items-center justify-between mb-6 pb-4 border-b">
            <h2 className="text-lg font-medium">Filter Deals</h2>
            <button
              onClick={() => setFiltersOpen(false)}
              className="text-2xl leading-none"
              aria-label="Close filters"
            >
              ×
            </button>
          </div>
          {/* Applied Filters */}
          {appliedFilters.length > 0 && (
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">
                  {appliedFilters.length} Applied Filters
                </span>
                <button
                  onClick={clearFilters}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {appliedFilters.map((filter, idx) => (
                  <span
                    key={idx}
                    className="px-3 py-1 bg-gray-100 text-xs rounded-full"
                  >
                    {filter}
                  </span>
                ))}
              </div>
            </div>
          )}
          {/* Sort by */}
          <div className="mb-6">
            <h3 className="text-sm font-medium mb-3">Sort by</h3>
            <div className="space-y-2">
              {SORT_OPTIONS.map((option) => (
                <label key={option} className="flex items-center">
                  <input
                    type="radio"
                    name="sort-mobile"
                    value={option}
                    checked={sort === option}
                    onChange={(e) => setSort(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm">{option}</span>
                </label>
              ))}
            </div>
          </div>
          {/* Categories */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium">Categories</h3>
              <div className="flex gap-2">
                <button
                  onClick={selectAllCategories}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Select all
                </button>
                <button
                  onClick={clearAllCategories}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  Clear
                </button>
              </div>
            </div>
            <div className="space-y-2">
              {fetchedCategories.map((cat) => {
                const productCount =
                  getProductCountForCategory[cat.id.toString()] || 0;
                return (
                  <label
                    key={cat.id}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(cat.id.toString())}
                        onChange={() => toggleCategory(cat.id.toString())}
                        className="mr-2"
                        disabled={productCount === 0}
                      />
                      <span
                        className={`text-sm ${
                          productCount === 0 ? "text-gray-400" : ""
                        }`}
                      >
                        {cat.category_name}
                      </span>
                    </div>
                    <span className="text-xs text-gray-500">({productCount})</span>
                  </label>
                );
              })}
            </div>
          </div>
          {/* Price Range */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium">Price Range</h3>
              <span className="text-xs text-blue-600">
                {formatPriceWithCommas(priceRange[0])} -{" "}
                {formatPriceWithCommas(priceRange[1])}
              </span>
            </div>
            <PriceRangeUI
              compact
              priceStats={priceStats}
              priceRange={priceRange}
              priceDraft={priceDraft}
              onSliderChange={onPriceSliderChange}
              onInputFocus={onPriceInputFocus}
              onInputChange={onPriceInputChange}
              onInputCommit={onPriceInputCommit}
              formatPriceWithCommas={formatPriceWithCommas}
            />
          </div>
          <button
            onClick={() => setFiltersOpen(false)}
            className="w-full py-3 bg-black text-white font-medium mt-4"
          >
            SHOW {filteredCount} DEALS
          </button>
        </div>
      </div>
    </>
  );
}

const Deals = () => {
  // State
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Filters
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("Discount: High to Low");
  // Desktop sidebar (md+) + Mobile off-canvas (<md)
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  // ✅ draft input values (string) for user-friendly typing
  const [priceDraft, setPriceDraft] = useState(["0", "10000"]);
  const [editingPriceIndex, setEditingPriceIndex] = useState(null);
  // Categories fetched from API
  const [fetchedCategories, setFetchedCategories] = useState([]);
  // Refs
  const cardsRef = useRef([]);
  const headerRef = useRef(null);
  const gridRef = useRef(null);

  // Sort options specific to deals
  const SORT_OPTIONS = [
    "Discount: High to Low",
    "Discount: Low to High",
    "Price: Low to High",
    "Price: High to Low",
    "A-Z",
    "Z-A",
    "Newest First",
  ];

  // --- Image Handling ---
  const getProductImage = (product) => {
    if (!product) return FALLBACK_IMG;
    if (
      product.images &&
      Array.isArray(product.images) &&
      product.images.length > 0
    ) {
      const firstImage = product.images[0];
      if (firstImage?.url) return firstImage.url;
      if (firstImage?.image_path) return firstImage.image_path;
      if (firstImage?.filename) return `${imgLocation}/${firstImage.filename}`;
      if (typeof firstImage === "string") {
        if (
          firstImage.startsWith("http://") ||
          firstImage.startsWith("https://") ||
          firstImage.startsWith("/")
        ) {
          return firstImage;
        }
        return `${imgLocation}/${firstImage}`;
      }
    }
    if (product.img_path) {
      try {
        const parsed = JSON.parse(product.img_path);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const firstImg = parsed[0];
          if (typeof firstImg === "string") return `${imgLocation}/${firstImg}`;
        }
      } catch (e) {
        if (typeof product.img_path === "string") {
          return `${imgLocation}/${product.img_path}`;
        }
      }
    }
    return FALLBACK_IMG;
  };

  

  const formatPrice = (price) => {
  const p = parseFloat(price);
  if (!p || p <= 0) return "Price on request";
  return `₹${Math.round(p).toLocaleString("en-IN")}`;
};


const getEffectivePrice = (product) => {
  // Check if product has pre-calculated discount info from API
  if (product?.has_discount && product?.discounted_price && product?.original_price) {
    return {
      final: parseFloat(product.discounted_price),
      original: parseFloat(product.original_price),
      hasDiscount: true,
      percent: product.discount_percent || 0
    };
  }

  // Check for detailed prices array
  if (product?.prices && Array.isArray(product.prices) && product.prices.length > 0) {
    let bestPrice = Infinity;
    let bestOriginal = Infinity;
    let hasDiscount = false;
    
    product.prices.forEach(priceItem => {
      const originalPrice = priceItem.price || 0;
      let finalPrice = originalPrice;
      
      if (priceItem.single_discount_type && priceItem.single_discount_value) {
        const discType = priceItem.single_discount_type;
        const discValue = parseFloat(priceItem.single_discount_value);
        
        if (discType === 'percentage' && discValue > 0) {
          finalPrice = originalPrice * (1 - discValue / 100);
        } else if ((discType === 'amount' || discType === 'fixed') && discValue > 0) {
          finalPrice = originalPrice - discValue;
        }
        
        if (finalPrice < originalPrice) {
          hasDiscount = true;
        }
      }
      
      if (finalPrice < bestPrice) {
        bestPrice = finalPrice;
        bestOriginal = originalPrice;
      }
    });
    
    if (hasDiscount && bestPrice < bestOriginal) {
      return {
        final: bestPrice,
        original: bestOriginal,
        hasDiscount: true,
        percent: Math.round(((bestOriginal - bestPrice) / bestOriginal) * 100)
      };
    }
    
    return {
      final: bestPrice,
      original: bestPrice,
      hasDiscount: false,
      percent: 0
    };
  }

  // Check for string format with discounts
  if (product?.prices_string && product?.single_discount_types && product?.single_discount_values) {
    const priceArray = product.prices_string.split(',').map(p => parseFloat(p)).filter(p => !isNaN(p));
    const discountTypes = product.single_discount_types.split(',');
const discountValues = product.single_discount_values.split(',').map((v) => {
  const num = parseFloat(v);
  return isNaN(num) ? 0 : num;
});
    
    if (priceArray.length > 0) {
      let bestPrice = Infinity;
      let bestOriginal = Infinity;
      let hasDiscount = false;
      
      priceArray.forEach((price, idx) => {
        const originalPrice = price;
        let finalPrice = originalPrice;
        
        if (discountTypes[idx] && discountValues[idx] !== undefined) {
          if (discountTypes[idx] === 'percentage' && discountValues[idx] > 0) {
            finalPrice = originalPrice * (1 - discountValues[idx] / 100);
          } else if ((discountTypes[idx] === 'amount' || discountTypes[idx] === 'fixed') && discountValues[idx] > 0) {
            finalPrice = originalPrice - discountValues[idx];
          }
          
          if (finalPrice < originalPrice) {
            hasDiscount = true;
          }
        }
        
        if (finalPrice < bestPrice) {
          bestPrice = finalPrice;
          bestOriginal = originalPrice;
        }
      });
      
      if (hasDiscount && bestPrice < bestOriginal) {
        return {
          final: bestPrice,
          original: bestOriginal,
          hasDiscount: true,
          percent: Math.round(((bestOriginal - bestPrice) / bestOriginal) * 100)
        };
      }
      
      return {
        final: bestPrice,
        original: bestPrice,
        hasDiscount: false,
        percent: 0
      };
    }
  }

  // Check for direct discount fields
  if (
    product?.single_discount_types && 
    product?.single_discount_values && 
    product?.single_discount_types !== '' && 
    product?.single_discount_values !== '' &&
    parseFloat(product.single_discount_values) > 0
  ) {
    const originalPrice = parseFloat(product?.min_price || product?.price || 0);
    const discValue = parseFloat(product.single_discount_values);
    
    if (!isNaN(discValue) && discValue > 0 && originalPrice > 0) {
      let finalPrice = originalPrice;
      
      if (product.single_discount_types === 'percentage') {
        finalPrice = originalPrice * (1 - discValue / 100);
      } else if (product.single_discount_types === 'amount' || product.single_discount_types === 'fixed') {
        finalPrice = originalPrice - discValue;
      }
      
      if (finalPrice < originalPrice) {
        return {
          final: finalPrice,
          original: originalPrice,
          hasDiscount: true,
          percent: Math.round(((originalPrice - finalPrice) / originalPrice) * 100)
        };
      }
    }
  }

  // Fallback to simple price
  const priceValue = product?.min_price || product?.price || 0;
  return {
    final: priceValue,
    original: priceValue,
    hasDiscount: false,
    percent: 0
  };
};

// Replace formatPriceWithDiscount function
const formatPriceWithDiscount = (product) => {
  const priceInfo = getEffectivePrice(product);
  
  if (!priceInfo.final || priceInfo.final <= 0) {
    return <span className="text-sm font-medium text-gray-500">Price on request</span>;
  }

  // ✅ If discounted < original => show green + strike
  if (priceInfo.hasDiscount && priceInfo.final < priceInfo.original) {
    return (
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-green-600">
          {formatPrice(priceInfo.final)}
        </span>
        <span className="text-xs text-gray-400 line-through">
          {formatPrice(priceInfo.original)}
        </span>
      </div>
    );
  }

  // Normal price
  return <span className="text-sm font-medium text-gray-900">{formatPrice(priceInfo.final)}</span>;
};

// Replace getDiscountBadge function
const getDiscountBadge = (product) => {
  const priceInfo = getEffectivePrice(product);
  
  if (priceInfo.hasDiscount && priceInfo.final < priceInfo.original) {
    return (
      <div className="absolute left-2 z-10 bg-red-500 text-white text-xs px-2 py-1 rounded">
        {priceInfo.percent}% OFF
      </div>
    );
  }
  
  return null;
};

  const getFirstPrice = (product) => {
    const priceInfo = getEffectivePrice(product);
    return priceInfo.final;
  };

  const formatPriceWithCommas = (price) => {
    const safe = Number.isFinite(Number(price)) ? Number(price) : 0;
    return `₹${safe.toLocaleString("en-IN")}`;
  };

  const getDescriptionSummary = (text, maxWords = 18) => {
    const plain = String(text || "")
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    if (!plain) return "No description available";
    const words = plain.split(" ");
    return words.length <= maxWords
      ? plain
      : words.slice(0, maxWords).join(" ") + "…";
  };

  // --- Fetch categories ---
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesResponse = await $.getJSON(
          `${ApiURl}/getMainCategory.php`
        );
        if (categoriesResponse?.data) {
          const activeCategories = categoriesResponse.data.filter(
            (c) => c.is_active === "1"
          );
          setFetchedCategories(activeCategories);
          const categoryIds = activeCategories.map((cat) => cat.id.toString());
          setSelectedCategories(categoryIds);
        }
      } catch (err) {
        console.error("Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, []);

  // --- Fetch all discounted products ---
useEffect(() => {
  const fetchDeals = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await $.ajax({
        url: `${ApiURl}/getallproducts.php`,
        method: "GET",
        dataType: "json",
        timeout: 10000,
      }).catch((err) => {
        throw new Error(
          `Failed to fetch products: ${err.statusText || err.message}`
        );
      });

      let productList = [];
      if (response?.status === "success" && Array.isArray(response?.products)) {
        productList = response.products;
      } else if (Array.isArray(response)) {
        productList = response;
      } else if (response?.data) {
        productList = response.data;
      }

      const discountedProducts = productList
        .filter((product) => product?.has_discount && Number(product.discount_percent) > 0)
        .map((product) => {
          let categoryName = product.category_name || "Uncategorized";
          categoryName = String(categoryName).trim();
          if (categoryName) {
            categoryName = categoryName
              .toLowerCase()
              .split(" ")
              .filter(Boolean)
              .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
              .join(" ");
          }

          const categoryId = (
            product.category_id ||
            product.main_category_id ||
            ""
          )
            .toString()
            .trim();

          return {
            ...product,
            product_id: product.product_id.toString(),
            original_product_id: product.product_id,
            name: product.product_name || product.name,
            product_name: product.product_name || product.name,
            category_id: categoryId,
            category_name: categoryName,
            min_price: parseFloat(product.discounted_price || product.min_price || 0),
            unique_key: `${product.product_id}`,
          };
        })
        .sort((a, b) => (b.discount_percent || 0) - (a.discount_percent || 0));

      setDeals(discountedProducts);

      const prices = discountedProducts
        .map((p) => parseFloat(p.discounted_price || p.min_price || 0))
        .filter((p) => p > 0);

      const minP = prices.length ? Math.floor(Math.min(...prices)) : 0;
      const maxP = prices.length ? Math.ceil(Math.max(...prices)) : 10000;
      const safeMin = Math.max(0, minP);
      const safeMax = Math.max(safeMin, maxP);

      setPriceRange([safeMin, safeMax]);
    } catch (err) {
      console.error("Error fetching deals:", err);
      setError(
        `Error loading deals: ${err.message}. Please check if the server is running and CORS is configured.`
      );
      setDeals([]);
    } finally {
      setLoading(false);
    }
  };

  fetchDeals();
}, []);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    if (mq.matches) setFiltersOpen(true);
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape") setFiltersOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Animation
  useEffect(() => {
    if (!headerRef.current || !gridRef.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        headerRef.current,
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power3.out" }
      );

      gsap.fromTo(
        gridRef.current,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: "power3.out", delay: 0.2 }
      );
    });

    return () => ctx.revert();
  }, []);

  const getProductCountForCategory = useMemo(() => {
    const counts = {};
    fetchedCategories.forEach((cat) => {
      const catId = cat.id.toString();
      counts[catId] = deals.filter(
        (p) =>
          p.category_id === catId ||
          (p.category_name || "").toLowerCase() ===
            (cat.category_name || "").toLowerCase()
      ).length;
    });
    return counts;
  }, [fetchedCategories, deals]);

  const priceStats = useMemo(() => {
    if (deals.length === 0) return { min: 0, max: 10000 };
    const prices = deals.map(getFirstPrice).filter((p) => p > 0);
    if (!prices.length) return { min: 0, max: 10000 };
    const min = Math.floor(Math.min(...prices));
    const max = Math.ceil(Math.max(...prices));
    return { min: Math.max(0, min), max: Math.max(min, max) };
  }, [deals]);

  useEffect(() => {
    setPriceRange((prev) => {
      const [a, b] = prev;
      const min = priceStats.min;
      const max = priceStats.max;
      const clampedA = Math.min(Math.max(a, min), max);
      const clampedB = Math.min(Math.max(b, min), max);
      const finalMin = Math.min(clampedA, clampedB);
      const finalMax = Math.max(clampedA, clampedB);
      if (finalMin === a && finalMax === b) return prev;
      return [finalMin, finalMax];
    });
  }, [priceStats.min, priceStats.max]);

  // ✅ keep draft synced ONLY when not editing (prevents cursor/focus issues)
  useEffect(() => {
    if (editingPriceIndex !== null) return;
    setPriceDraft([String(priceRange[0]), String(priceRange[1])]);
  }, [priceRange[0], priceRange[1], editingPriceIndex]);

  const toggleCategory = (categoryId) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const selectAllCategories = () => {
    const allCategoryIds = fetchedCategories.map((cat) => cat.id.toString());
    setSelectedCategories(allCategoryIds);
  };

  const clearAllCategories = () => {
    setSelectedCategories([]);
  };

  const handlePriceSliderChange = (index, value) => {
    const v = Number.parseInt(value, 10);
    if (Number.isNaN(v)) return;
    setPriceRange((prev) => {
      const next = [...prev];
      next[index] = v;
      if (index === 0 && next[0] > next[1]) next[1] = next[0];
      if (index === 1 && next[1] < next[0]) next[0] = next[1];
      next[0] = Math.min(Math.max(next[0], priceStats.min), priceStats.max);
      next[1] = Math.min(Math.max(next[1], priceStats.min), priceStats.max);
      return next;
    });
  };

  const handlePriceInputChange = (index, rawValue) => {
    const cleaned = String(rawValue ?? "").replace(/[^\d]/g, "");
    setPriceDraft((prev) => {
      const next = [...prev];
      next[index] = cleaned; // allow empty while typing
      return next;
    });
    const v = Number.parseInt(cleaned, 10);
    if (!Number.isNaN(v)) {
      setPriceRange((prev) => {
        const next = [...prev];
        next[index] = Math.min(Math.max(v, priceStats.min), priceStats.max);
        if (index === 0 && next[0] > next[1]) next[1] = next[0];
        if (index === 1 && next[1] < next[0]) next[0] = next[1];
        return next;
      });
    }
  };

  const commitPriceInput = (index) => {
    setPriceDraft((prev) => {
      const next = [...prev];
      const parsed = Number.parseInt(next[index], 10);
      if (Number.isNaN(parsed)) {
        next[index] = String(priceRange[index]);
      }
      return next;
    });
    setEditingPriceIndex(null);
  };

  useEffect(() => {
    const newApplied = [];
    if (
      selectedCategories.length > 0 &&
      selectedCategories.length < fetchedCategories.length
    ) {
      const selectedCatNames = selectedCategories
        .map((id) => fetchedCategories.find((c) => c.id.toString() === id))
        .filter(Boolean)
        .map((c) => c.category_name)
        .filter((name) => name && name !== "Uncategorized");
      if (selectedCatNames.length > 0) {
        if (selectedCatNames.length <= 2) newApplied.push(...selectedCatNames);
        else newApplied.push(`${selectedCatNames.length} categories`);
      }
    }
    if (priceRange[0] > priceStats.min || priceRange[1] < priceStats.max) {
      newApplied.push(
        `₹ ${priceRange[0].toLocaleString()} - ₹ ${priceRange[1].toLocaleString()}`
      );
    }
    if (query.trim()) newApplied.push(`Search: "${query.trim()}"`);
    if (sort && sort !== "Discount: High to Low") newApplied.push(`Sort: ${sort}`);
    setAppliedFilters(newApplied);
  }, [selectedCategories, fetchedCategories, priceRange, priceStats, query, sort]);

  const clearFilters = () => {
    setQuery("");
    setSort("Discount: High to Low");
    const allCategoryIds = fetchedCategories.map((cat) => cat.id.toString());
    setSelectedCategories(allCategoryIds);
    setPriceRange([priceStats.min, priceStats.max]);
  };

  const filteredProducts = useMemo(() => {
    let list = [...deals];
    
    if (selectedCategories.length > 0) {
      list = list.filter((p) => {
        const productCatId = (p.category_id || "").toString();
        const productCatName = (p.category_name || "").toLowerCase();
        return selectedCategories.some((selectedCatId) => {
          const selectedCat = fetchedCategories.find(
            (c) => c.id.toString() === selectedCatId
          );
          if (!selectedCat) return false;
          if (productCatId === selectedCat.id.toString()) return true;
          if (
            productCatName === (selectedCat.category_name || "").toLowerCase()
          )
            return true;
          return false;
        });
      });
    }
    
    list = list.filter((p) => {
      const price = getFirstPrice(p);
      return price >= priceRange[0] && price <= priceRange[1];
    });
    
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const searchText = `${p.product_name || ""} ${p.description || ""} ${
          p.category_name || ""
        }`.toLowerCase();
        return searchText.includes(q);
      });
    }
    
    if (sort === "A-Z") {
      list.sort((a, b) =>
        (a.product_name || "").localeCompare(b.product_name || "")
      );
    } else if (sort === "Z-A") {
      list.sort((a, b) =>
        (b.product_name || "").localeCompare(a.product_name || "")
      );
    } else if (sort === "Price: Low to High") {
      list.sort((a, b) => getFirstPrice(a) - getFirstPrice(b));
    } else if (sort === "Price: High to Low") {
      list.sort((a, b) => getFirstPrice(b) - getFirstPrice(a));
    } else if (sort === "Discount: Low to High") {
      list.sort((a, b) => (a.discount_percent || 0) - (b.discount_percent || 0));
    } else if (sort === "Discount: High to Low") {
      list.sort((a, b) => (b.discount_percent || 0) - (a.discount_percent || 0));
    } else if (sort === "Newest First") {
      list.sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      );
    }
    
    return list;
  }, [deals, selectedCategories, fetchedCategories, priceRange, query, sort]);

  useEffect(() => {
    const els = cardsRef.current.filter(Boolean);
    if (!els.length) return;
    gsap.killTweensOf(els);
    gsap.fromTo(
      els,
      { opacity: 0, y: 18, scale: 0.98 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.55,
        ease: "power2.out",
        stagger: 0.06,
      }
    );
  }, [filteredProducts]);

  const onCardEnter = (i) => {
    const el = cardsRef.current[i];
    if (!el) return;
    gsap.to(el, {
      y: -10,
      boxShadow: "0px 45px 60px 0px rgba(0, 0, 0, 0.087)",
      duration: 0.22,
      ease: "power2.out",
    });
  };

  const onCardLeave = (i) => {
    const el = cardsRef.current[i];
    if (!el) return;
    gsap.to(el, {
      y: 0,
      boxShadow: "0px 15px 20px 0px rgba(0, 0, 0, 0.057)",
      duration: 0.22,
      ease: "power2.out",
    });
  };

  if (error) {
    return (
      <div className="min-h-screen w-full bg-[var(--user-bg,#E8D9D5)] py-6 font-['Poppins',sans-serif] mt-10">
        <div className="mx-auto w-full max-w-7xl px-4 py-20">
          <div className="text-center">
            <div className="text-lg font-medium text-gray-900 mb-2">
              Connection Error
            </div>
            <div className="text-sm text-gray-600 mb-4">{error}</div>
            <button
              onClick={() => window.location.reload()}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
            >
              Retry Loading
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[var(--user-bg,#E8D9D5)] py-6 font-['Poppins',sans-serif] mt-10">
      <div className="mx-auto w-full max-w-7xl px-4">

        {/* Filter Bar */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs tracking-[0.08em] text-neutral-600">
              {filteredProducts.length} deal{filteredProducts.length !== 1 ? "s" : ""} available
              {deals.length > 0 && ` (${deals.length} total)`}
            </p>
          </div>
          {/* Mobile Filter Button */}
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="md:hidden border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50 flex items-center gap-2"
          >
            <span>☰</span>
            Filter Deals
          </button>
          {/* Desktop Toggle */}
          <div className="hidden md:flex items-center gap-3">
            {appliedFilters.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-neutral-600">
                  {appliedFilters.length} Applied Filters
                </span>
                <button
                  onClick={clearFilters}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Clear all
                </button>
              </div>
            )}
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className="border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50"
            >
              {filtersOpen ? "Hide Filters" : "Show Filters"}
            </button>
          </div>
        </div>

        <div ref={gridRef} className="flex gap-6">
          {/* Desktop Filters Sidebar */}
          <div
            className={`hidden md:block w-64 flex-shrink-0 bg-white ${
              filtersOpen ? "md:block" : "md:hidden"
            }`}
          >
            <div className="border border-neutral-200 p-5 sticky top-24">
              {/* Search */}
              <div className="mb-8">
                <h3 className="text-sm font-medium mb-4 uppercase tracking-wider">
                  Search Deals
                </h3>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search products..."
                  className="w-full border border-neutral-200 bg-white px-3 py-2 text-sm outline-none placeholder:text-neutral-400 focus:border-neutral-300 rounded"
                />
              </div>

              {/* Sort by */}
              <div className="mb-8">
                <h3 className="text-sm font-medium mb-4 uppercase tracking-wider">
                  Sort by
                </h3>
                <div className="space-y-2">
                  {SORT_OPTIONS.map((option) => (
                    <label key={option} className="flex items-center">
                      <input
                        type="radio"
                        name="sort-desktop"
                        value={option}
                        checked={sort === option}
                        onChange={(e) => setSort(e.target.value)}
                        className="mr-3"
                      />
                      <span className="text-sm">{option}</span>
                    </label>
                  ))}
                </div>
              </div>
              {/* Categories */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium uppercase tracking-wider">
                    Categories
                  </h3>
                  <div className="flex gap-2">
                    <button
                      onClick={selectAllCategories}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Select all
                    </button>
                    <button
                      onClick={clearAllCategories}
                      className="text-xs text-blue-600 hover:text-blue-800"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {fetchedCategories.map((cat) => {
                    const productCount =
                      getProductCountForCategory[cat.id.toString()] || 0;
                    return (
                      <label
                        key={cat.id}
                        className="flex items-center justify-between"
                      >
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedCategories.includes(
                              cat.id.toString()
                            )}
                            onChange={() => toggleCategory(cat.id.toString())}
                            className="mr-3"
                            disabled={productCount === 0}
                          />
                          <span
                            className={`text-sm ${
                              productCount === 0 ? "text-gray-400" : ""
                            }`}
                          >
                            {cat.category_name}
                          </span>
                        </div>
                        <span className="text-xs text-gray-500">
                          ({productCount})
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
              {/* Price Range */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-medium uppercase tracking-wider">
                    Price Range
                  </h3>
                  <span className="text-xs text-blue-600 font-medium">
                    {formatPriceWithCommas(priceRange[0])} -{" "}
                    {formatPriceWithCommas(priceRange[1])}
                  </span>
                </div>
                <PriceRangeUI
                  priceStats={priceStats}
                  priceRange={priceRange}
                  priceDraft={priceDraft}
                  onSliderChange={handlePriceSliderChange}
                  onInputFocus={(i) => setEditingPriceIndex(i)}
                  onInputChange={handlePriceInputChange}
                  onInputCommit={commitPriceInput}
                  formatPriceWithCommas={formatPriceWithCommas}
                />
              </div>
              {/* Clear Button */}
              <button
                onClick={clearFilters}
                className="w-full py-3 border border-neutral-200 text-sm font-medium text-neutral-800 hover:bg-neutral-50 transition-colors"
              >
                Clear all filters
              </button>
            </div>
          </div>
          {/* Products Grid */}
          <div className="flex-1">
            <div className="pt-5">
              {loading ? (
                <div className="flex justify-center py-12">
                  <div className="text-lg text-gray-600">Loading deals...</div>
                </div>
              ) : filteredProducts.length === 0 ? (
                <div className="mt-10 border border-neutral-200 bg-white p-6 text-center rounded-lg">
                  <div className="text-sm uppercase tracking-[0.12em] text-neutral-900">
                    {deals.length === 0
                      ? "No discounted products found"
                      : "No deals match your filters"}
                  </div>
                  <div className="mt-2 text-xs tracking-[0.08em] text-neutral-600">
                    {deals.length === 0
                      ? "There are no products with active discounts at the moment."
                      : "Try adjusting categories, price range, or search."}
                  </div>
                  {deals.length > 0 && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="mt-4 border border-neutral-200 bg-white px-3 py-2 text-xs uppercase tracking-[0.12em] text-neutral-800 hover:bg-neutral-50 rounded transition-colors"
                    >
                      Clear All Filters
                    </button>
                  )}
                </div>
              ) : (
                <div
                  className="
                    grid justify-center gap-4
                    [grid-auto-rows:minmax(380px,auto)]
                    grid-cols-2
                    sm:grid-cols-2
                    md:grid-cols-3
                    lg:grid-cols-3
                    xl:grid-cols-4
                  "
                >
                  {filteredProducts.map((product, index) => {
                    const imageUrl = getProductImage(product);
                    const priceDisplay = formatPriceWithDiscount(product);
                    const discountBadge = getDiscountBadge(product);
                    
                    return (
                      <div
                        key={product.unique_key || product.product_id || index}
                        ref={(el) => (cardsRef.current[index] = el)}
                        onMouseEnter={() => onCardEnter(index)}
                        onMouseLeave={() => onCardLeave(index)}
                        className="
                          relative flex flex-col bg-white p-3
                          shadow-[0px_15px_20px_0px_rgba(0,0,0,0.057)]
                          will-change-transform
                          hover:shadow-lg transition-shadow duration-200
                          rounded-lg border border-gray-100
                          overflow-hidden h-full
                        "
                      >
                        <div className="relative">
                          {discountBadge}
                         <div className="flex w-full items-center justify-between mb-3 text-[11px] tracking-[0.1em] text-neutral-700">
  <div /> {/* left side placeholder */}
  <div className="bg-gray-50 px-2 py-1 rounded">
    {priceDisplay}
  </div>
</div>
                        </div>
                        <Link
                          to={`/ProductOverview/${
                            product.original_product_id || product.product_id
                          }`}
                          className="flex w-full items-center justify-center no-underline mb-3"
                        >
                          <div className="w-full min-h-[200px] flex items-center justify-center bg-neutral-50 rounded-lg overflow-hidden">
                            <img
                              src={imageUrl}
                              alt={
                                product.product_name || product.name || "Product"
                              }
                              className="block w-full h-full object-contain p-2"
                              loading="lazy"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = FALLBACK_IMG;
                              }}
                            />
                          </div>
                        </Link>
                        <div className="flex w-full items-center justify-between mb-2">
                          <div className="max-w-[85%] truncate uppercase text-neutral-900 text-sm font-semibold">
                            {product.product_name ||
                              product.name ||
                              `Product ${product.product_id}`}
                          </div>
                          <Link
                            to={`/ProductOverview/${
                              product.original_product_id || product.product_id
                            }`}
                            className="text-base leading-none text-neutral-700 no-underline hover:text-neutral-900 transition-colors"
                            aria-label="View product"
                            title="View product"
                          >
                            🛒
                          </Link>
                        </div>
                        <div className="flex-1 border-t border-neutral-200 pt-2">
                          <p
                            className="text-xs tracking-[0.08em] text-neutral-600"
                            style={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {getDescriptionSummary(
                              product.description
                            )}
                          </p>
                        </div>
                        {product.discount_percent > 0 && (
                          <div className="mt-2">
                            <span className="inline-flex items-center bg-green-100 text-green-700 text-xs px-2 py-1 rounded">
                              Save {product.discount_percent}%
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* ✅ Mobile Filter Sidebar (stable component, no remount focus loss) */}
      <MobileFilterSidebar
        filtersOpen={filtersOpen}
        setFiltersOpen={setFiltersOpen}
        appliedFilters={appliedFilters}
        clearFilters={clearFilters}
        sort={sort}
        setSort={setSort}
        SORT_OPTIONS={SORT_OPTIONS}
        fetchedCategories={fetchedCategories}
        selectedCategories={selectedCategories}
        toggleCategory={toggleCategory}
        selectAllCategories={selectAllCategories}
        clearAllCategories={clearAllCategories}
        getProductCountForCategory={getProductCountForCategory}
        priceRange={priceRange}
        priceStats={priceStats}
        priceDraft={priceDraft}
        onPriceInputFocus={(i) => setEditingPriceIndex(i)}
        onPriceSliderChange={handlePriceSliderChange}
        onPriceInputChange={handlePriceInputChange}
        onPriceInputCommit={commitPriceInput}
        formatPriceWithCommas={formatPriceWithCommas}
        filteredCount={filteredProducts.length}
      />
    </div>
  );
};

export default Deals;
